// JC 기준정보 해석 (§7-59) — **자원 위에서 앞뒤로 붙은 두 활동이 JC 를 얼마나 만드는가.**
//
// 보정에서 작업 순서를 바꾸면 이웃이 바뀌고, 이웃이 바뀌면 JC 도 다시 정해져야 한다.
// 엔진은 계획을 다시 세울 때 그걸 하지만 화면은 계획을 다시 세우지 않으므로,
// **같은 기준정보를 같은 규칙으로 읽어서** 화면에서 답을 낸다.
//
// ## 체인 — 첫 매치가 이긴다
//
// 엔진 `Resource:1126 initJobChangeInformers` 의 등록 순서 그대로다:
//
//   ① routeMx      공정 쌍        TB_FP_JC_TIME
//   ② routeGrpMx   공정그룹 쌍    TB_FP_JC_TIME_GRP
//   ③ itemMx       품목 쌍        TB_FP_JC_TIME_ITEM
//   ④ itemGrpMx    품목그룹 쌍    TB_FP_JC_TIME_ITEM_GRP
//   ⑤ resources.routeMs      자원의 기본 Route     route_jc_time
//   ⑥ resources.routeGrpMs   자원의 기본 RouteGrp  route_grp_jc_time
//   ⑦ resources.baseMs       자원 기본            jc_time
//
// ## ⚠️ 틀리기 쉬운 자리 넷 — 전부 엔진이 비대칭이라서 생긴다
//
// **(가) 게이트가 ①~⑥ 에만 걸린다.**
// `JobChangeInformerAbstract:10` 의 `isNoJobChangeTimeCondition = isRouteNone || isSameRoute` 는
// 그 추상 클래스를 **상속한 정보제공자에게만** 적용된다. ⑦ 의 `JcInfoOnDefault` 는 그 클래스를
// 상속하지 않고 `JobChangeInformer` 를 직접 구현해서 **게이트를 통과하지 않는다.**
// → **같은 공정끼리도 자원 `jc_time` 은 붙는다.** 지금 데이터가 "자원마다 같은 공정만"인데도
//   화면에 prev JC 가 보이는 이유가 이것이다.
//
// **(가-2) 하지만 ⑦ 에도 자기 가드가 있다 — 이웃이 아예 없으면 안 붙는다.**
//   `if (fromActivity == null || toActivity == null) return NO_DECISION;`
// → **자원의 첫 작업에는 prev JC 가 없다.** (가)의 "게이트를 무시한다"를 "무조건 붙는다"로
//   읽으면 여기서 틀린다. ⑦ 이 무시하는 것은 **공정 조건**이지 **이웃의 부재**가 아니다.
//   ⚠️ 이 둘을 헷갈려 첫 작업에 JC 를 붙였다가 실데이터로 반증됐다(2026-09-18).
//
// **(나) `0` 의 뜻이 매트릭스와 기본값에서 정반대다.**
// - 매트릭스(①~④)에 **행이 있으면** 그 값이 `0` 이어도 **결정**이다 → 체인이 거기서 멈춘다.
//   ("이 쌍은 교체 없음"이라는 등록이다.)
// - 기본값(⑤~⑦)의 `0` 은 **값 없음**이다 → 다음 단계로 내려간다.
//   (엔진이 `NO_DECISION` 을 참조 비교로 판별한다 — `hasJobChangeInfo`.)
//
// **(다) ⑤⑥⑦ 은 `next` 를 만들 수 없다.**
// 엔진의 단일 시간 생성자가 `JobChangeInfo(time, dt) → this(TIME_ZERO, time, dt)` 라
// 받은 값을 전부 **prev 에** 넣는다(`JobChangeInfo:23`). `next` JC 는 3인자 생성자를 쓰는
// **매트릭스에서만** 나온다.
//
// ## 단위·정규화는 여기서 하지 않는다
//
// 백엔드가 ms 로 맞춰서 준다(`AdjustmentGanttService.getJcMaster`). 원본은 행마다 `time_uom`
// 이 다를 수 있는데 그 환산을 여기 복제하면 엔진과 조용히 갈린다.
//
// ## ⚠️ 실데이터로 검증되지 않는 범위
//
// 2026-09-18 기준 매트릭스 네 테이블이 **전부 0 행**이고 자원 기본값만 설정돼 있다.
// 즉 실제로 도는 건 ⑦ 뿐이고 **①~④ · next JC · 쌍별 분할타입은 단위 테스트로만 검증된다.**
// 데이터가 생기면 실환경에서 다시 확인할 것.

/** 활동에서 쌍 키를 뽑는 네 가지 방법 — 체인 ①~④ 에 1:1 대응한다. */
const PAIR_AXES = [
  { mx: "routeMx", of: (act) => act?.routeCd },
  { mx: "routeGrpMx", of: (act, index) => index.routeGroups?.[act?.routeCd] },
  { mx: "itemMx", of: (act) => act?.itemCd },
  { mx: "itemGrpMx", of: (act) => act?.itemGrpCd }
];

const pairKey = (resourceCd, from, to) => `${resourceCd} ${from} ${to}`;

/**
 * 번들을 조회용 인덱스로 바꾼다. **조회 1회에 한 번만** 부르면 된다.
 *
 * 매트릭스는 자원 × 쌍이라 커질 수 있어서 `Map` 으로 O(1) 조회를 만든다 — 보정 조작 한 번마다
 * 몇 번씩 찾게 되므로 선형 탐색이면 §7-49 에서 고친 렉이 그대로 돌아온다.
 */
export const buildJcIndex = (bundle) => {
  const index = {
    resources: new Map(Object.entries(bundle?.resources ?? {})),
    routeGroups: bundle?.routeGroups ?? {}
  };
  PAIR_AXES.forEach(({ mx }) => {
    const map = new Map();
    (bundle?.[mx] ?? []).forEach(row => {
      // ⚠️ `prevMs`/`nextMs` 가 0 인 행도 담는다 — 위 (나) 참고. 존재 자체가 결정이다.
      map.set(pairKey(row.resourceCd, row.from, row.to), row);
    });
    index[mx] = map;
  });
  return index;
};

/** 빈 번들로도 안전하게 돌도록 — 조회 전이나 실패 시 쓴다. */
export const EMPTY_JC_INDEX = buildJcIndex(null);

const isNoJobChangeTimeCondition = (fromRouteCd, toRouteCd) => {
  // 엔진 `JobChangeInformerAbstract` — 공정이 없거나(`isRouteNone`) 같으면(`isSameRoute`)
  // ①~⑥ 은 통째로 건너뛴다. ⑦ 은 이 판정을 보지 않는다.
  if (!fromRouteCd || !toRouteCd) return true;
  return fromRouteCd === toRouteCd;
};

const nonNegative = (value) => (typeof value === "number" && value > 0 ? value : 0);

/**
 * 앞 활동(`fromAct`)과 뒤 활동(`toAct`) 사이의 JC 를 정한다.
 *
 * @param fromAct  앞 활동. `null` 이면 자원의 첫 작업이다
 * @param toAct    뒤 활동 — JC 가 붙는 쪽(**JC 는 뒤 활동의 소유다**)
 * @param index    `buildJcIndex` 결과
 * @param resourceCd 대상 자원. 행간 이동이면 **옮겨갈 자원**을 넘긴다
 *
 * @returns `{ prevMs, nextMs, tp, by }` — `by` 는 어느 단계가 이겼는지(진단·테스트용).
 *          `toAct` 가 없으면 `null`.
 */
export const resolveJc = (fromAct, toAct, index, resourceCd) => {
  if (!toAct || !resourceCd) return null;

  const base0 = index?.resources?.get(resourceCd);

  // ⚠️ **앞 활동이 아예 없으면 JC 가 없다** — 자원의 첫 작업이다.
  // ①~⑥ 은 게이트(`isRouteNone` 이 `activity == null` 을 true 로 본다)에 걸려 건너뛰고,
  // ⑦ 도 `JcInfoOnDefault` 자신의 가드에 걸린다:
  //   `if (fromActivity == null || toActivity == null) return NO_DECISION;`
  // ⑦ 이 무시하는 것은 **게이트**(공정 없음·같은 공정)이지 **이웃의 부재**가 아니다.
  // 둘을 헷갈려 첫 작업에 JC 를 붙였다가 실데이터로 반증됐다(2026-09-18).
  if (!fromAct) return { prevMs: 0, nextMs: 0, tp: base0?.tp ?? "Y", by: "noPrev" };

  const from = fromAct;
  const gated = isNoJobChangeTimeCondition(from?.routeCd, toAct?.routeCd);

  // ①~④ 매트릭스. 게이트에 걸리면 통째로 건너뛴다.
  if (!gated && from) {
    for (let i = 0; i < PAIR_AXES.length; i += 1) {
      const axis = PAIR_AXES[i];
      const fromKey = axis.of(from, index);
      const toKey = axis.of(toAct, index);
      if (!fromKey || !toKey) continue;

      const row = index[axis.mx]?.get(pairKey(resourceCd, fromKey, toKey));
      // ⚠️ 행의 **존재**가 결정이다 — 값이 0 이어도 여기서 멈춘다.
      if (row) {
        return {
          prevMs: nonNegative(row.prevMs),
          nextMs: nonNegative(row.nextMs),
          tp: row.tp ?? "Y",
          by: axis.mx
        };
      }
    }
  }

  const base = index.resources?.get(resourceCd);
  if (!base) return { prevMs: 0, nextMs: 0, tp: "Y", by: "none" };
  const tp = base.tp ?? "Y";

  // ⑤⑥ — 자원의 기본 Route / RouteGroup. 여기도 게이트를 본다.
  // ⚠️ 셋 다 **prev 로만** 들어간다 — 위 (다) 참고.
  if (!gated && from) {
    // ⑤ `JcInfoOnDefaultRoute` — 공정이 다를 때(게이트가 이미 보장한다).
    const routeMs = nonNegative(base.routeMs);
    if (routeMs > 0) return { prevMs: routeMs, nextMs: 0, tp, by: "routeDefault" };

    // ⑥ `JcInfoOnDefaultRouteGroup` — ⚠️ **공정그룹까지 달라야** 한다.
    // 게이트는 공정만 보므로 "공정은 다른데 같은 그룹"이면 여기서 걸러진다.
    // 엔진의 `if (!fromRG.equals(toRG))` 가 그 조건이고, 빠뜨리면 같은 그룹 안의
    // 공정 전환에도 JC 가 붙어 엔진과 갈린다.
    const fromGrp = index.routeGroups?.[from.routeCd];
    const toGrp = index.routeGroups?.[toAct.routeCd];
    const routeGrpMs = nonNegative(base.routeGrpMs);
    if (routeGrpMs > 0 && fromGrp && toGrp && fromGrp !== toGrp) {
      return { prevMs: routeGrpMs, nextMs: 0, tp, by: "routeGrpDefault" };
    }
  }

  // ⑦ — 자원 기본. **게이트를 무시한다.** 같은 공정끼리도 붙는다.
  // (첫 작업은 위에서 이미 걸러졌다 — `JcInfoOnDefault` 의 자기 가드, (가-2) 참고.)
  const baseMs = nonNegative(base.baseMs);
  if (baseMs > 0) return { prevMs: baseMs, nextMs: 0, tp, by: "base" };

  return { prevMs: 0, nextMs: 0, tp, by: "none" };
};

// ──────────────────────────────────────────────────────────────────────────
// 박스 재구성 — 여기서 **JC 박스가 생기고 사라진다**
// ──────────────────────────────────────────────────────────────────────────
//
// ⚠️ 지금까지 파이프라인은 **"박스 집합은 불변"을 암묵 전제**했다. 순서가 바뀌면 JC 는
// `0 → 양수`(새로 생김) · `양수 → 0`(사라짐) · `prev 없는데 next 생김` 이 전부 일어난다.
// 그래서 이 함수만 박스를 만들고 지운다 — 다른 곳은 좌표만 옮긴다.

const PREV_SUFFIX = "_PREVJC";
const NEXT_SUFFIX = "_NEXTJC";

/** 소유 활동에서 JC 박스를 만든다. **좌표는 활동에 딱 붙인 임시값**이고 규칙이 다시 앉힌다. */
const makeJcBox = (owner, kind, jcTimeMs, tp) => {
  const prev = kind === "prev";
  return {
    id: `${owner.id}${prev ? PREV_SUFFIX : NEXT_SUFFIX}`,
    s: prev ? owner.s - jcTimeMs : owner.e,
    e: prev ? owner.s : owner.e + jcTimeMs,
    type: "jc",
    jcKind: kind,
    jcTime: jcTimeMs,
    netMs: jcTimeMs,
    color: owner.color,
    text: owner.text,
    late: false,
    hasTool: false,
    inBound: owner.inBound,
    changed: true,
    // ⚠️ **분할타입은 기준정보가 준 값을 쓴다** — 활동의 것이 아니다. 쌍마다 다를 수 있고
    // 휴무에서의 배치를 통째로 바꾼다. 소유 활동의 나머지 필드는 표시용으로 물려받는다.
    activity: { ...owner.activity, divideTpCd: tp, jcYn: "Y" }
  };
};

/**
 * 인접이 바뀐 활동들의 JC 를 기준정보로 **다시 만든다**.
 *
 * ⚠️ **전체를 다시 계산하지 않는다**(2026-09-18 결정). 조회 직후 상태는 백엔드가 준 값이
 * 정본이고, 사용자가 순서를 바꾼 쌍만 여기서 갱신한다. 전체를 돌리면 *조회만 해도* 화면이
 * 바뀌어서, 프런트 해석과 엔진 저장값의 차이가 사용자에게 "데이터가 변했다"로 보인다.
 *
 * @param boxes       행의 박스 전체(JC 포함)
 * @param keys        JC 를 다시 정할 **소유 활동 키** 집합. `null` 이면 전부(테스트·행간 이동용)
 * @param index       `buildJcIndex` 결과
 * @param resourceCd  이 행의 자원. 행간 이동이면 **옮겨간 행의** 자원이다
 * @param ownerKeyOf  박스 id → 소유 활동 키(`groupKeyOf`)
 *
 * @returns `{ boxes, addedKeys, removedKeys }` — 뒤 둘은 호출부가 변경 배지·되돌리기에 쓴다
 */
export const reassignJc = (boxes = [], { keys = null, index, resourceCd, ownerKeyOf }) => {
  if (!index || !resourceCd || !ownerKeyOf) return { boxes, addedKeys: new Set(), removedKeys: new Set() };

  const mains = boxes.filter(box => box.type !== "jc").slice().sort((a, b) => a.s - b.s);
  if (mains.length === 0) return { boxes, addedKeys: new Set(), removedKeys: new Set() };

  const jcByOwner = new Map();
  boxes.forEach(box => {
    if (box.type !== "jc") return;
    const key = ownerKeyOf(box.id);
    if (!jcByOwner.has(key)) jcByOwner.set(key, {});
    jcByOwner.get(key)[box.jcKind === "next" ? "next" : "prev"] = box;
  });

  const addedKeys = new Set();
  const removedKeys = new Set();
  const rebuilt = [];

  mains.forEach((main, i) => {
    const ownerKey = ownerKeyOf(main.id);
    const existing = jcByOwner.get(ownerKey) ?? {};

    // 건드리지 않은 활동은 **원래 박스를 그대로** 둔다 — 같은 객체여야 변경 감지가 오탐하지 않는다.
    if (keys && !keys.has(ownerKey)) {
      if (existing.prev) rebuilt.push(existing.prev);
      rebuilt.push(main);
      if (existing.next) rebuilt.push(existing.next);
      return;
    }

    const prevMain = i > 0 ? mains[i - 1] : null;
    const nextMain = i + 1 < mains.length ? mains[i + 1] : null;

    // prev JC 는 **앞 활동 → 나** 의 값, next JC 는 **나 → 뒤 활동** 의 값이다.
    const asTo = resolveJc(prevMain?.activity ?? null, main.activity, index, resourceCd);
    const asFrom = nextMain ? resolveJc(main.activity, nextMain.activity, index, resourceCd) : null;

    const prevMs = nonNegative(asTo?.prevMs);
    // ⚠️ next JC 는 **내 뒤 이웃과의 쌍**이 정한다. 뒤가 없으면 next 도 없다.
    const nextMs = nonNegative(asFrom?.nextMs);

    if (prevMs > 0) {
      if (!existing.prev) addedKeys.add(ownerKey);
      rebuilt.push(makeJcBox(main, "prev", prevMs, asTo.tp));
    } else if (existing.prev) {
      removedKeys.add(ownerKey);
    }

    rebuilt.push(main);

    if (nextMs > 0) {
      if (!existing.next) addedKeys.add(ownerKey);
      rebuilt.push(makeJcBox(main, "next", nextMs, asFrom.tp));
    } else if (existing.next) {
      removedKeys.add(ownerKey);
    }
  });

  return { boxes: rebuilt, addedKeys, removedKeys };
};
